import { trechoDaSessao } from "./sessao";
/**
 * Integração RH ⇆ DP — navegação entre sistemas com login único (SSO via URL).
 * Os três apps rodam localmente em portas diferentes; ao trocar de sistema
 * levamos o usuário logado pela URL (#sso=...) para não pedir login de novo.
 *
 * Portas da SERCAMP (faixa 4001-4005; não colidem com a NX ENERGY que usa
 * 3000/3001/3002): RH = 4001 · DP = 4002 · Portal = 8080.
 */
/** URL do Portal: localhost em dev; apex do dominio em producao (sem hardcode do dominio). */
function portalHost(): string {
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    const isLocal = h === "localhost" || h === "127.0.0.1" || h === "::1";
    if (!isLocal) {
      // O Portal fica no apex; este sistema esta num subdominio -> remove o 1o rotulo.
      const root = h.replace(/^www\./, "").split(".").slice(1).join(".") || h;
      return `${window.location.protocol}//${root}`;
    }
  }
  return "http://localhost:8080";
}
export const SYSTEMS = {
  portal: portalHost(),
  rh: "http://localhost:4001",
  dp: "http://localhost:4002",
} as const;

/** Lê o usuário vindo do Portal/outro sistema pela URL (#sso=...). */
export function readSsoUser(): string | null {
  const m = window.location.hash.match(/(?:^|[#&])sso=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Remove o token SSO da URL após o login (deixa a barra de endereço limpa). */
export function clearSsoFromUrl() {
  if (window.location.hash.includes("sso=")) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/** Navega para outro sistema levando o usuário logado (login único). */
export function goToSystem(url: string, user: string | null) {
  window.location.href = user ? `${url}/#sso=${encodeURIComponent(user)}${trechoDaSessao()}` : url;
}

/** systemId deste sistema (usado na checagem de acesso no Portal). */
export const SYSTEM_ID = "rh";

/** Login global do Admin Supremo — sempre liberado e sempre admin. */
export const ADMIN_SUPREMO_LOGIN = "admin@portalsercamp.com";

/** É o Admin Supremo? (comparação em minúsculas, com trim). */
export function isAdminSupremo(user: string | null | undefined): boolean {
  return (user ?? "").trim().toLowerCase() === ADMIN_SUPREMO_LOGIN;
}

export interface AcessoResult {
  allowed: boolean;
  adminSupremo: boolean;
  /** Acesso somente visualização (cadastro do Portal). */
  somenteLeitura: boolean;
  /** Abas liberadas neste sistema; null = todas. */
  abas: string[] | null;
}

/**
 * Pergunta ao Portal se <user> tem acesso a ESTE sistema.
 * GET {portal}/api/acesso?u=<usuario>&sistema=rh
 *   -> { allowed, adminSupremo, somenteLeitura, abas }.
 * Em caso de falha de rede, propaga o erro (o chamador trata de forma segura,
 * sem liberar o app).
 */
export async function checkAcesso(user: string): Promise<AcessoResult> {
  const url =
    `${SYSTEMS.portal}/api/acesso` +
    `?u=${encodeURIComponent(user)}&sistema=${encodeURIComponent(SYSTEM_ID)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Portal respondeu ${res.status}`);
  const body = (await res.json()) as Partial<AcessoResult>;
  return {
    allowed: !!body.allowed,
    adminSupremo: !!body.adminSupremo,
    somenteLeitura: body.somenteLeitura === true,
    abas: body.somenteLeitura === true && Array.isArray(body.abas) ? body.abas.map(String) : null,
  };
}
